import { Button } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import { IBestDataset, IPlotVariable } from '../../../../../../types/analytics';
import { IGetBasisChartV2ApiPayload } from '../../../../../../types/chart';
import { MarkerItem } from '../../../../../../types/home';
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput';
import { IEnrichedMetadata } from '../../../../../../types/metadata';
import { useTimeSeriesParams } from '../../../hooks/useTimeSeriesParams';
import { AnalyticsSection } from '../../Layout/AnalyticsSection';
import { TimeSeriesChart } from './TimeSeriesChart';
import { TSPlotInputForm } from './TimeSeriesInputForm';

interface TSPlotProps {
  onDatasetChange: (item: string) => void;
  datasets: IBestDataset[];
  variables: IPlotVariable[];
  defaultValueShortName?: string | null;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  previewDatasetId: string;
  previewDatasetTimeRange: [moment.Moment, moment.Moment];
  tsData: { [key: string]: any }[];
  onUpdate: (values: IGetBasisChartV2ApiPayload) => void;
  tsError: string;
  updatedTSData: any[];
  pointProps: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  loadingButtonUpdate: boolean;
  errorUpdate: string;
  point?: MarkerItem;
  onChangeActiveKey: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  pointOption: { label: string; value: string }[];
  isOffline: boolean;
  coordinates: number[];
  enrichedMetadata: IEnrichedMetadata[];
}

export default function TSPlot(props: TSPlotProps) {
  const {
    tsError,
    previewDatasetId,
    loadingButtonUpdate,
    errorUpdate,
    pointName,
    variables,
    onDatasetChange,
    isOffline,
  } = props;

  const {
    tsPlotData,
    variable,
    tempVariable,
    name,
    startDate,
    endDate,
    categories,
    memoCoords,
    selectedDatasetInfo,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onUpdateTSPlot,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  } = useTimeSeriesParams(props);

  // TODO: enable cache
  // const upsertTimeSeriesCache = useAnalyticsCacheStore(state => state.upsertTimeSeriesCache)

  // useEffect(() => {
  //   if (!previewDatasetId || !variable?.Index) return

  //   const key = `${previewDatasetId}_${variable.Index}_${memoCoords.lat},${memoCoords.lng}`

  //   upsertTimeSeriesCache(key, {
  //     variableXIndex: variable.Index,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     timeSeriesData: tsPlotData,
  //     name,
  //     variable,
  //     selectedDatasetInfo
  //   })
  // }, [upsertTimeSeriesCache, previewDatasetId, variable?.Index, startDate, endDate, props.isForAllAnalytics, tsPlotData, memoCoords, name, variable, selectedDatasetInfo]);

  const plotInputContent = useMemo(
    () => (
      <TSPlotInputForm
        datasets={props.datasets}
        previewDatasetId={previewDatasetId}
        onDatasetChange={onDatasetChange}
        categories={categories}
        variables={variables}
        tempVariable={tempVariable}
        onVariableChange={doVariableChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={doStartDateChange}
        onEndDateChange={doEndDateChange}
        disabledStartDate={disabledStartDate}
        disabledEndDate={disabledEndDate}
        isForAllAnalytics={props.isForAllAnalytics}
        onCheckboxChange={handleCheckboxChange}
        isLoading={loadingButtonUpdate}
        hasError={!!tsError}
      />
    ),
    [
      props.datasets,
      previewDatasetId,
      onDatasetChange,
      categories,
      variables,
      tempVariable,
      doVariableChange,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
      props.isForAllAnalytics,
      handleCheckboxChange,
      loadingButtonUpdate,
      tsError,
    ]
  );

  const updateContent = useMemo(
    () => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '36px',
        }}
      >
        <Button
          type='primary'
          data-testid={`Analytics-Update_Button`}
          disabled={loadingButtonUpdate || !!errorUpdate || !!tsError}
          loading={loadingButtonUpdate}
          onClick={() => onUpdateTSPlot()}
          style={{
            width: '90px',
            height: '36px',
          }}
        >
          Update
        </Button>
      </div>
    ),
    [loadingButtonUpdate, errorUpdate, tsError, onUpdateTSPlot]
  );

  const emptyText = useMemo(() => {
    if (previewDatasetId.includes('CMEMS')) {
      return 'Layer not available';
    }
  }, [previewDatasetId]);

  const chartContent = useMemo(
    () => (
      <TimeSeriesChart
        tsPlotData={tsPlotData}
        pointName={pointName}
        coordinates={memoCoords}
        variable={variable}
        name={name}
        selectedDatasetInfo={selectedDatasetInfo}
        isLoading={loadingButtonUpdate}
        error={errorUpdate || tsError}
        isOffline={isOffline}
        emptyData={emptyText}
      />
    ),
    [
      tsPlotData,
      pointName,
      memoCoords,
      variable,
      name,
      selectedDatasetInfo,
      loadingButtonUpdate,
      errorUpdate,
      tsError,
      emptyText,
    ]
  );

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={null}
      isLoading={loadingButtonUpdate}
    />
  );
}
